import { useEffect, useRef, useState, useCallback } from 'react';
import { getRiskEvaluation } from './flowPlannerApi';
import { RISK_STATUS } from './riskEngineModel';

const DEFAULT_POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = new Set([RISK_STATUS.COMPLETED, RISK_STATUS.FAILED]);

/** True for HTTP 4xx — client errors won't recover from retry. */
const isClientError = (status) => Math.floor(status / 100) === 4;

/**
 * Polls the Risk Engine evaluation for a plan on a fixed cadence. Stops
 * once a terminal status (COMPLETED or FAILED) is observed, or when the
 * caller passes `enabled=false`. Resets on planId change.
 *
 * @returns {{ riskEvaluation: object|null, isPolling: boolean, error: Error|null }}
 */
export default function useRiskEvaluationPolling(planId, enabled, intervalMs = DEFAULT_POLL_INTERVAL_MS) {
  const [riskEvaluation, setRiskEvaluation] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  useEffect(() => {
    if (!enabled || !planId) {
      stopPolling();
      return undefined;
    }

    cancelledRef.current = false;
    setRiskEvaluation(null);
    setError(null);
    setIsPolling(true);

    const poll = () => {
      getRiskEvaluation(planId)
        .then((result) => {
          if (cancelledRef.current) return;
          setRiskEvaluation(result);
          setError(null);
          if (result && TERMINAL_STATUSES.has(result.status)) {
            stopPolling();
          } else {
            timerRef.current = setTimeout(poll, intervalMs);
          }
        })
        .catch((err) => {
          if (cancelledRef.current) return;
          setError(err);
          // Stop polling on client errors (4xx) — those won't recover from retry
          // (e.g. wrong query param, missing auth). Keep polling on 5xx and
          // unknown errors so transient blips don't permanently block the UI.
          if (isClientError(err?.status)) {
            stopPolling();
            return;
          }
          timerRef.current = setTimeout(poll, intervalMs);
        });
    };

    poll();

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [planId, enabled, intervalMs, stopPolling]);

  return { riskEvaluation, isPolling, error };
}
